// libs
import isEmpty from 'lodash/isEmpty';

// helpers
import { stringifyFilters } from 'shared/layouts/Listing/helpers';
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import {
  getUndefinedIfNotValid,
  pushRetinaDataInDatalayer,
} from 'shared/helpers/analytics/helpers';
import { getIsCAVClickRedirectedPage } from '../ProductsRow/helpers';
import storage from 'shared/helpers/storage';

// constants
import { CHANNEL, CLICK_INTERACTION_IDENTIFIER } from './constants';
import {
  PLP_EVENTS,
  SEARCH,
  CATEGORY_LISTING_PAGE,
  BRAND_LISTING_PAGE,
  PLP_INTERACTION,
  LISTING_PAGE_TYPE,
} from 'shared/helpers/analytics/constants/plpEvents';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { SEARCH_TYPE } from 'shared/components/GlobalSearch/constants';
let isInteracted = false;

//types
import { EntityData, MerchandisingDetails } from 'shared/store/listing/types';

const parseRelatedSearchesWidgetPosition = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/** Fields for `search_listing_loads` / category PLP when listing API returned related searches. */
const getRelatedSearchesAnalyticsFields = (
  relatedSearches?: Listing.IRelatedSearchData | null,
  listingHasRelatedSearchWidget?: boolean,
  relatedSearchAnchorInfo?: string
) => {
  if (relatedSearches == null && !listingHasRelatedSearchWidget) {
    return {};
  }
  const sets = relatedSearches?.sets;
  const hasRS =
    !!listingHasRelatedSearchWidget ||
    (Array.isArray(sets) && sets.length > 0);
  const firstSet = Array.isArray(sets) && sets.length > 0 ? sets[0] : undefined;
  const fromAnalytics = parseRelatedSearchesWidgetPosition(relatedSearches?.rsAnalyticsPositions?.[0]);
  const fromSet = parseRelatedSearchesWidgetPosition(firstSet?.position);
  const related_searches_widget_position =
    fromAnalytics !== undefined ? fromAnalytics : fromSet;

  return {
    is_related_searches_present: hasRS,
    ...(related_searches_widget_position !== undefined && {
      related_searches_widget_position,
    }),
    ...(typeof relatedSearchAnchorInfo === 'string' &&
      relatedSearchAnchorInfo.trim() !== '' && {
      related_searches_anchor_info: relatedSearchAnchorInfo,
    })
  };
};

interface ISearchPageLoadProps {
  title: string;
  impressiveProducts: string;
  queryExpansionTerm: string;
  queryExpansionType: string;
  guidesAvailable?: string;
  isPriceReveal: boolean;
  isSalePriceLiveEnabled: boolean;
  totalCount: number;
  entityData: EntityData;
  search: string;
  merchandisingDetails: MerchandisingDetails;
  products: Listing.IProduct[];
  isFomoTimerVisible: boolean;
  adPlatformPageType: string;
  trackingMetadata: Record<string, any>;
  transition_trigger_type?: string;
  transition_location?: string;
  store_callout?: string;
  relatedSearches?: Listing.IRelatedSearchData | null;
  listingHasRelatedSearchWidget?: boolean;
}
interface GetSearchPageloadDataFromUrl {
  totalCount: number;
  entityData: EntityData;
  queryExpansionTerm: string;
  queryExpansionType: string;
  search: string;
}

const getCommaSeparatedString = (array: (number | string)[], removeDuplicates = false) => {
  if (array.length > 0) {
    if (removeDuplicates) {
      array = array.filter((item, index) => array.indexOf(item) === index && item !== undefined);
    }
    return array.join(',');
  }
  return '';
};

const getIsSalePriceLive = (isSalePriceLiveEnabled: boolean, products: Listing.IProduct[]) => {
  let isSalePriceLive = undefined;

  if (isSalePriceLiveEnabled && products?.length > 0) {
    isSalePriceLive = products.some((product: Listing.IProduct) => product.is_sale_participating);
  }

  return isSalePriceLive;
};

// Helper to calculate cumulative OOS count for pagination
export const calculateCumulativeOOSCount = ({
  products,
  currentPage,
  previousCumulativeCount,
  isFetchingWithFilters,
}: {
  products: Listing.IProduct[];
  currentPage: number | string;
  previousCumulativeCount: number;
  isFetchingWithFilters?: boolean;
}): number => {
  let currentPageOOSCount = 0;
  for (let i = 0; i < products.length; i++) {
    if (products[i].isOutOfStock) {
      currentPageOOSCount++;
    }
  }

  const isFirstPageOrFilterChange = String(currentPage) === '1' || isFetchingWithFilters;

  return isFirstPageOrFilterChange
    ? currentPageOOSCount
    : previousCumulativeCount + currentPageOOSCount;
};
interface GetSearchPageloadDataFromUrl {
  totalCount: number;
  entityData: EntityData;
  queryExpansionTerm: string;
  queryExpansionType: string;
  isPriceRevealEnabled: boolean;
  isSalePriceLiveEnabled: boolean;
  products: Listing.IProduct[];
  isFomoTimerVisible: boolean;
  merchandisingDetails: MerchandisingDetails;
  adPlatformPageType: string;
  trackingMetadata: Record<string, any>;
  cartSaleData?: Record<number, boolean>;
  specialTagList?: Record<number, any>;
}

export const getSearchPageloadDataFromUrl = ({
  totalCount,
  entityData,
  queryExpansionTerm = '',
  queryExpansionType = '',
  search,
  isPriceRevealEnabled,
  isSalePriceLiveEnabled = false,
  products = [],
  isFomoTimerVisible,
  merchandisingDetails,
  adPlatformPageType,
  trackingMetadata,
  cartSaleData,
  specialTagList,
}: GetSearchPageloadDataFromUrl) => {
  const {
    is_hybrid_page: isHybridPage,
    has_pinned_products: hasPinnedProducts,
    has_tiptiles: hasTiptiles,
    has_banners: hasBanners,
  } = merchandisingDetails;
  const searchParams = new URLSearchParams(search);
  const searchKeyword = getUndefinedIfNotValid(searchParams.get('q'));
  let isPriceRevealProductAvailable = undefined;
  let priceParticipatingSku: boolean = false;
  const priceParticipatingSkuList: { rule: string; }[] = []; // [{ rule }]

  const priceRevealList: typeof products = [];
  let isSpecialTagsPresentInTopN: boolean = false;
  const specialTagNamesList: string[] = [];
  let OOSCount = 0;

  if (isPriceRevealEnabled && products?.length > 0) {
    isPriceRevealProductAvailable = products.some(
      (product: Listing.IProduct) => !isEmpty(product?.price_reveal_data)
    );
  }

  const n = products.length;

  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (String(product?.type || '').toLowerCase() === 'related_searches') continue;
    if (isEmpty(product?.price_reveal_data) === false) {
      priceRevealList.push(product);
    }

    OOSCount += product.isOutOfStock ? 1 : 0;

    isSpecialTagsPresentInTopN = products
      .slice(0, n)
      .some((product) => !isEmpty(product.specialTag))
      ? true
      : false;

    if (i < n) {
      let tagName = product?.specialTag?.type || '';

      if (!tagName && specialTagList?.[product?.id]?.tag) {
        tagName = specialTagList[product?.id].tag;
      }

      tagName && specialTagNamesList.push(tagName);
    }

    if (product?.is_sale_participating === true) {
      priceParticipatingSku = true;
    }

    if (product?.is_sale_participating === true) {
      priceParticipatingSkuList.push({
        rule: 'MRP-DP',
      });
    } else if (cartSaleData && cartSaleData[product?.id]) {
      priceParticipatingSkuList.push({
        rule: 'CART Rule',
      });
    }
  }

  const searchType = getUndefinedIfNotValid(searchParams.get('searchType'));
  const isFromRelatedSearches = searchType === SEARCH_TYPE.RELATED_SEARCHES;
  const pageName = isFromRelatedSearches
    ? `${SEARCH}:${SEARCH_TYPE.RELATED_SEARCHES}`
    : `${SEARCH}:${searchKeyword}`;

  const rsFields: Record<string, any> = {};
  if (isFromRelatedSearches) {
    const rsClickPos = searchParams.get('rsClickPosition');
    const rsWidgetPos = searchParams.get('rsWidgetPosition');
    const rsAnchor = searchParams.get('rsAnchorInfo');
    const rsImageId = searchParams.get('rsImageId');
    const rsInteractionLoc = searchParams.get('rsInteractionLocation');

    if (rsClickPos) rsFields.related_searches_click_position = Number(rsClickPos);
    if (rsWidgetPos) rsFields.related_searches_widget_position = Number(rsWidgetPos);
    if (rsAnchor) rsFields.related_searches_anchor_info = rsAnchor;
    if (rsImageId) rsFields.related_searches_image_id = rsImageId;
    if (rsInteractionLoc) rsFields.interaction_location = rsInteractionLoc;

    rsFields.listing_opened = `${SEARCH}:${SEARCH_TYPE.RELATED_SEARCHES}`;
    rsFields.search_location = rsInteractionLoc || undefined;
    rsFields.site_navigation = `${SEARCH}:${SEARCH_TYPE.RELATED_SEARCHES}`;
    rsFields.site_sub_navigation = `${SEARCH}:${searchKeyword}`;
  }

  const searchPagedata = {
    searchKeyword,
    searchType,
    searchTypedKeyword: getUndefinedIfNotValid(searchParams.get('typedSearchTerm')),
    page: pageName,
    suggestionType: getUndefinedIfNotValid(searchParams.get('suggestionType')),
    queryExpansionDetails: `${queryExpansionTerm}|${queryExpansionType}`,
    totalProductCount: totalCount,
    entityDetails:
      entityData?.entityType &&
      `${entityData?.entityCoverage} | ${entityData?.entityType} | ${entityData.entityValues}`,
    isPriceRevealProductAvailable,
    isSalePriceLive: getIsSalePriceLive(isSalePriceLiveEnabled, products),
    isFomoTimerVisible,
    isHybridPlp: isHybridPage,
    hasPinnedProducts: hasPinnedProducts,
    hasTiptiles: hasTiptiles,
    hasBanners: hasBanners,
    hybridPageType: isHybridPage ? adPlatformPageType : '',
    trackingMetadata: trackingMetadata,
    entityCoverage: entityData?.entityCoverage,
    entityType: entityData?.entityType,
    entityValues: entityData?.entityValues,
    priceRevealList,
    isSpecialTagsPresentInTopN,
    specialTagNamesList,
    priceParticipatingSku,
    priceParticipatingSkuList,
    OOSCount,
    ...rsFields,
  };

  return searchPagedata;
};

export const onSearchPageLoad = ({
  queryExpansionTerm = '',
  queryExpansionType = '',
  isPriceReveal,
  isSalePriceLiveEnabled,
  totalCount,
  entityData,
  search,
  merchandisingDetails,
  products = [],
  isFomoTimerVisible,
  adPlatformPageType,
  trackingMetadata,
  transition_trigger_type,
  transition_location,
  store_callout,
  relatedSearches,
  listingHasRelatedSearchWidget,
}: ISearchPageLoadProps) => {
  const { is_hybrid_page: isHybridPage } = merchandisingDetails;
  const pageData = getSearchPageloadDataFromUrl({
    totalCount,
    entityData,
    queryExpansionTerm,
    queryExpansionType,
    search,
    isPriceRevealEnabled: isPriceReveal,
    isSalePriceLiveEnabled,
    products,
    isFomoTimerVisible: isFomoTimerVisible,
    merchandisingDetails,
    adPlatformPageType,
    trackingMetadata,
  });

  const searchParams = new URLSearchParams(search);
  const internalSearchTerm = getUndefinedIfNotValid(searchParams.get('internalSearchTerm'));
  const relatedSearchAnchorInfo = getUndefinedIfNotValid(searchParams.get('typedSearchTerm'));

  pushRetinaDataInDatalayer({
    plpSearchTerm: internalSearchTerm,
    plpDnHybridPageType: isHybridPage ? adPlatformPageType : '',
  });

  NFA.track({
    type: TRACK_TYPE.PAGELOAD,
    event: PLP_EVENTS.SEARCH_PAGELOAD,
    data: {
      /* URL attribution (e.g. rsWidgetPosition from parent SLP/CLP) must win over widget position on this page */
      ...getRelatedSearchesAnalyticsFields(relatedSearches, listingHasRelatedSearchWidget, relatedSearchAnchorInfo),
      ...pageData,
      wishlistClickCount: {
        addEvents: 0,
        removeEvents: 0,
      },
      viewSimilarClickCount: 0,
      transition_trigger_type,
      transition_location,
      store_callout,
    },
  });
};

const getProductIdTagListString = (tags: Listing.IProductTag[] = [], id: string) => {
  if (isEmpty(tags)) return `${id}:no tag`;
  const tagNames = tags.map((tag) => tag.title);
  return `${id}:${tagNames.join('|')}`;
};

const pushIfValid = (arr: string[], element: any) => {
  if (!element) return arr;
  arr.push(element);
  return arr;
};

const productIdBadgeListString = (badge: string, id: string) => {
  if (!badge) return `${id}:no badge`;

  return `${id}:${badge}`;
};

export const getCategoryPageLoadData = (
  products: Listing.IProduct[] = [],
  isBrand: boolean,
  title: string,
  filterGuides: Listing.ITransfromedGuide,
  isPriceRevealEnabled: boolean,
  isSalePriceLiveEnabled: boolean,
  isFomoTimerVisible: boolean,
  cartSaleData?: Record<number, boolean>,
  specialTagList?: Record<number, any>
) => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const searchKeyword = getUndefinedIfNotValid(
      searchParams.get('q') || searchParams.get('internalSearchTerm')
    );
    const page = isBrand ? `brand:${title}` : `category:${title}`;
    const pageType = isBrand ? BRAND_LISTING_PAGE : CATEGORY_LISTING_PAGE;
    let OOSCount = 0;
    const productNameList: string[] = [];
    const productIdList: string[] = [];
    const brandNameList: string[] = [];
    let productIdTagList: string[] = [];
    let productsBadgeList: string[] = [];
    let priceParticipatingSku: boolean = false;
    let isPriceRevealProductAvailable = undefined;
    const priceRevealList: typeof products = [];
    let isSpecialTagsPresentInTopN: boolean = false;
    const specialTagNamesList: string[] = [];
    const priceParticipatingSkuList: { rule: string; }[] = []; // [{ rule }]

    const filterGuidesList = Object.values(filterGuides).map(
      (filter: Listing.IFilterGuide) => filter.type
    );

    const n = products.length;
    const isOnSalePresentInTopN = products
      .slice(0, n)
      .some((product: any) => product?.onSale === true);

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (product.type === 'tiptile') continue;
      if (String(product.type || '').toLowerCase() === 'related_searches') continue;

      productIdList.push(product.id);
      productNameList.push(product.subTitle);
      brandNameList.push(product.title);
      productsBadgeList = pushIfValid(
        productsBadgeList,
        productIdBadgeListString(product?.badge?.title, product?.id)
      );
      productIdTagList = pushIfValid(
        productIdTagList,
        getProductIdTagListString(product?.tag, product?.id)
      );
      OOSCount += product.isOutOfStock ? 1 : 0;

      if (isPriceRevealEnabled) {
        if (isEmpty(product?.price_reveal_data) === false) {
          isPriceRevealProductAvailable = true;
        } else {
          isPriceRevealProductAvailable = false;
        }
      }

      if (!isEmpty(product?.price_reveal_data)) {
        priceRevealList.push(product);
      }

      if (product?.is_sale_participating === true) {
        priceParticipatingSku = true;
      }

      isSpecialTagsPresentInTopN = products
        .slice(0, n)
        .some((product) => !isEmpty(product.specialTag))
        ? true
        : false;

      if (i < n) {
        let tagName = product?.specialTag?.type || '';

        if (!tagName && specialTagList?.[product?.id]?.tag) {
          tagName = specialTagList[product?.id].tag;
        }

        tagName && specialTagNamesList.push(tagName);
      }

      if (product?.is_sale_participating === true) {
        priceParticipatingSkuList.push({
          rule: 'MRP-DP',
        });
      } else if (cartSaleData && cartSaleData[product?.id]) {
        priceParticipatingSku = true;
        priceParticipatingSkuList.push({
          rule: 'CART Rule',
        });
      }
    }

    return {
      page,
      searchKeyword,
      categoryPageTitle: title,
      listingPageType: pageType,
      productNameList: getCommaSeparatedString(productNameList),
      productIdList: getCommaSeparatedString(productIdList),
      brandNameList: getCommaSeparatedString(brandNameList, true),
      productIdTagList: getCommaSeparatedString(productIdTagList),
      filterGuides: getCommaSeparatedString(filterGuidesList),
      productsBadgeList: getCommaSeparatedString(productsBadgeList),
      OOSCount,
      isCAVClickedRedirectedPage: getIsCAVClickRedirectedPage(),
      isPriceRevealProductAvailable,
      isSalePriceLive: getIsSalePriceLive(isSalePriceLiveEnabled, products),
      isFomoTimerVisible,
      priceRevealList: priceRevealList,
      priceParticipatingSku: priceParticipatingSku,
      isOnSalePresentInTopN,
      isSpecialTagsPresentInTopN,
      specialTagNamesList,
      priceParticipatingSkuList,
    };
  } catch (err) {
    return {};
  }
};

export const getOfferPageLoadData = (products: Listing.IProduct[] = []) => {
  try {
    const productIdList: string[] = [];
    const brandNameList: string[] = [];

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (product.type === 'tiptile') continue;
      if (String(product.type || '').toLowerCase() === 'related_searches') continue;

      productIdList.push(product.id);

      brandNameList.push(product.title);
    }
    return {
      productIdList: getCommaSeparatedString(productIdList),
      brandNameList: getCommaSeparatedString(brandNameList, true),
    };
  } catch (err) {
    return {};
  }
};

export const onPageLoad = (
  title: string,
  isBrand: boolean,
  params: {
    categoryId?: string;
    routeParams: string;
    guidesAvailable?: boolean;
    merchandisingDetails: MerchandisingDetails;
  },
  isPriceReveal: boolean,
  isSalePriceLiveEnabled: boolean,
  totalCount: number,
  sortUsed: string,
  filterGuides: Listing.ITransfromedGuide,
  products: Listing.IProduct[],
  isFomoTimerVisible: boolean,
  adPlatformPageType: string,
  trackingMetadata: Record<string, any>,
  isCategoryLaunchingSoon?: boolean,
  cartSaleData?: Record<number, boolean>,
  specialTagList?: Record<number, any>,
  transition_trigger_type?: string,
  transition_location?: string,
  store_callout?: string,
  relatedSearches?: Listing.IRelatedSearchData | null,
  listingHasRelatedSearchWidget?: boolean,
) => {
  const { is_hybrid_page: isHybridPage } =
    params && params.merchandisingDetails ? params.merchandisingDetails : { is_hybrid_page: false };
  const category_path = `${CHANNEL}:${params?.routeParams?.replace(/\//g, ':')}`;

  const pageData = getCategoryPageLoadData(
    products,
    isBrand,
    title,
    filterGuides,
    isPriceReveal,
    isSalePriceLiveEnabled,
    isFomoTimerVisible,
    cartSaleData,
    specialTagList
  );

  pushRetinaDataInDatalayer({
    plpCategoryId: params.categoryId,
    plpCategoryName: title,
    plpDnHybridPageType: isHybridPage ? adPlatformPageType : '',
  });

  NFA.track({
    type: TRACK_TYPE.PAGELOAD,
    event: PLP_EVENTS.CATEGORY_PAGELOAD,
    data: {
      ...pageData,
      ...getRelatedSearchesAnalyticsFields(relatedSearches, listingHasRelatedSearchWidget),
      PLPId: params.categoryId,
      isBrandListing: isBrand,
      categoryId: getUndefinedIfNotValid(params.categoryId),
      totalProductCount: totalCount,
      sortUsed,
      category_description: category_path,
      hybridPageType: isHybridPage ? adPlatformPageType : '',
      trackingMetadata: trackingMetadata,
      wishlistClickCount: {
        addEvents: 0,
        removeEvents: 0,
      },
      viewSimilarClickCount: 0,
      is_category_launching_soon: isCategoryLaunchingSoon || false,
      transition_trigger_type,
      transition_location,
      store_callout,
    },
  });
};

export const onFiltersApplied = (
  appliedFilters: Listing.IAppliedFilters,
  onAppliedClick = false
) => {
  let filtersStrigified;
  let filtersIdStringified = '';
  const filtersArray: Record<string, string>[] = [];

  try {
    filtersStrigified = stringifyFilters(appliedFilters);
    const separateFilters = filtersStrigified.split('|');
    separateFilters.forEach((filter) => {
      const filterIDs =
        filtersIdStringified == '' ? `${filter.split(':')[1]}` : `,${filter.split(':')[1]}`;
      filtersIdStringified = filtersIdStringified + filterIDs;

      const [key, value] = filter.split(':');
      if (value) filtersArray.push({ [key]: value });
    });
  } catch (e) {
    filtersStrigified = '';
  }
  const data = {
    filteredPLPFilters: filtersStrigified,
    filterIds: filtersIdStringified,
    filtersArray: filtersArray,
  };

  if (!onAppliedClick) {
    NFA.updateDataLayer(data);
    return;
  }
  try {
    NFA.track({
      event: PLP_EVENTS.FILTER_APPLIED,
      data,
    });
  } catch (e) {
    //
  }
};

export const onSortApplied = (sortFilter: string) => {
  try {
    NFA.track({
      event: PLP_EVENTS.SORT_APPLIED,
      data: {
        sortValue: sortFilter,
      },
    });
  } catch (e) {
    //
  }
};

// reset the isInteracted
export const resetIsInteracted = () => {
  isInteracted = false;
};

// On PLP intercation like product card, banner, wishlist this function will
// capture the first intercation and untill next page view is fired this
// interaction will remain as first interaction for this plp page
// resetOnInteraction
// return if it is first click interaction
export const onClickInteraction = (
  interactionType: string = '',
  resetOnInteraction: boolean = false,
  clickedInteraction?: string
) => {
  //PLP first interaction new event
  const data = JSON.parse(storage.getItem('NYKAA_NEXT_CLICK_DATA'));
  if (data?.isPLPClickInteracted) {
    NFA.track({
      event: PLP_EVENTS.MP_PLP_FIRST_CLICK_INTERACTION_NEW,
      data: { PLPClickInteractionType: interactionType },
    });
    data.isPLPClickInteracted = undefined;
    storage.setItem('NYKAA_NEXT_CLICK_DATA', JSON.stringify(data));
  }

  // if this is not first plp interaction return
  if (isInteracted === true) {
    NFA.updateDatalayerBeforeNext({ isPLPFirstClickInteraction: false });
    return false;
  }

  // this is first plp interaction
  isInteracted = true;
  const interactionData = { [CLICK_INTERACTION_IDENTIFIER]: 'true' };

  const trackData: Record<string, string> = { PLPClickInteractionType: interactionType };
  if (clickedInteraction) {
    trackData.clicked_interaction = clickedInteraction;
  }

  NFA.track({
    event: PLP_EVENTS.MP_PLP_FIRST_CLICK_INTERACTION,
    data: trackData,
  });

  if (interactionType === PLP_INTERACTION.PRODUCT) {
    NFA.updateDatalayerBeforeNext({ isPLPFirstClickInteraction: true });
  }

  storeAnalyticsDataInLocal(interactionData);

  if (resetOnInteraction) {
    resetIsInteracted();
  }

  return true;
};

export const onOfferPageLoad = (
  products: Listing.IProduct[],
  productCount: number,
  couponOfferId: string,
  couponCode: string
) => {
  NFA.firePageView();
  const pageData = getOfferPageLoadData(products);
  const dataOfPLP = {
    brandNameList: pageData?.brandNameList,
    productIdList: pageData?.productIdList,
    totalProductCount: productCount,
    pageType: LISTING_PAGE_TYPE.OFFER_PAGE,
    offer_id: couponOfferId,
    couponCode: couponCode || '',
  };
  NFA.track({
    event: PLP_EVENTS.OFFER_LISTING_PAGE_LOADS,
    data: dataOfPLP,
  });
};

export const onClearFilters = () => {
  try {
    NFA.track({
      event: PLP_EVENTS.PLP_FILTER_CLEAR,
    });
  } catch (e) {
    //
  }
};

export const onNoResults = (isSearch: boolean) => {
  try {
    if (isSearch) {
      NFA.track({
        event: PLP_EVENTS.SEARCH_NO_RESULTS,
      });
    } else {
      NFA.track({
        event: PLP_EVENTS.PLP_NO_RESULTS,
      });
    }
  } catch (e) {
    //
  }
};

export const onPaginationLoad = (
  currentPage?: number,
  totalProducts?: number,
  cumulativeOOSCount?: number
) => {
  if (!__BROWSER__) return;
  // Use cumulative OOS count passed from service (calculated at service level)

  NFA.track({
    event: PLP_EVENTS.PLP_PAGINATION_LOADED,
    data: {
      currentPage,
      totalProducts,
      OOSCount: cumulativeOOSCount || 0,
    },
  });
};

export const exitFilterDrawer = () => {
  NFA.track({
    event: PLP_EVENTS.PLP_REFINE_BACK_USED,
  });
};
